import { Moment } from 'moment-timezone';

import {
    QhpForSchedulingCalendar,
    SchedulingCalendar,
} from '../../components/calendars/SchedulingCalendar';
import { usePatient } from '../../fetching/api/patients';
import { useRouteOptimizerPageData } from '../../fetching/api/routeOptimizer';
import { SECTION_SKELETON_HEIGHT, SimpleSkeleton } from '../../fetching/suspense';
import { findOrError } from '../../utils/iteration';

export const FlexibleSchedulingDialogsCalendarWrapper = ({
    patientId,
    positions,
    assignableQhps,
    eligibleQhpsByPosition,
    assignmentsByPosition,
    newStartTime,
    showOnlyEligibleQhps,
    showOnlyCareTeamQhps,
}: {
    patientId: string;
    positions: {
        uuid: string;
        startOffsetMinutes: number | null;
        durationMinutes: number;
        services: { name: string }[];
    }[];
    assignableQhps: QhpForSchedulingCalendar[] | undefined;
    eligibleQhpsByPosition: Record<string, QhpForSchedulingCalendar[]> | undefined;
    assignmentsByPosition: Record<string, { uuid: string }>;
    newStartTime: Moment;
    showOnlyEligibleQhps: boolean;
    showOnlyCareTeamQhps: boolean;
}) => {
    // Queries.
    const patient = usePatient(patientId);
    // NOTE from Nick: Why are we using useRouteOptimizerPageData here? We really should have a separate request for reschedule.
    const { confirmedVisits, assignableShifts, assignableQhpShiftBlocks } =
        useRouteOptimizerPageData({
            startDate: newStartTime,
            endDate: newStartTime,
        });

    if (assignableQhps === undefined || eligibleQhpsByPosition === undefined) {
        return <SimpleSkeleton height={SECTION_SKELETON_HEIGHT} />;
    }

    const isQhpEligible = (qhp: QhpForSchedulingCalendar) =>
        Object.values(eligibleQhpsByPosition).some((eligibleQhpsForPosition) =>
            eligibleQhpsForPosition.some((eligibleQhp) => eligibleQhp.uuid === qhp.uuid),
        );
    const isQhpInCareTeam = (qhp: QhpForSchedulingCalendar) =>
        patient.care_team.some((careTeamQhp) => careTeamQhp.uuid === qhp.uuid);

    return (
        <SchedulingCalendar
            selectedDate={newStartTime}
            qhps={assignableQhps.filter(
                (qhp) =>
                    (!showOnlyEligibleQhps || isQhpEligible(qhp)) &&
                    (!showOnlyCareTeamQhps || isQhpInCareTeam(qhp)),
            )}
            shifts={assignableShifts}
            confirmedVisits={confirmedVisits}
            shiftBlocks={assignableQhpShiftBlocks}
            setVisitToEdit={() => undefined}
            tentativeVisits={Object.entries(assignmentsByPosition).map(
                ([positionId, qhp]: [string, { uuid: string }]) => {
                    const position = findOrError(
                        positions,
                        (position) => position.uuid === positionId,
                    );
                    return {
                        uuid: positionId,
                        title: `${patient.name}: ${position.services
                            .map((service) => service.name)
                            .join(', ')}`,
                        start: newStartTime
                            .clone()
                            .add(position.startOffsetMinutes ?? 0, 'minutes')
                            .toDate(),
                        end: newStartTime
                            .clone()
                            .add(position.startOffsetMinutes ?? 0, 'minutes')
                            .add(position.durationMinutes, 'minutes')
                            .toDate(),
                        resourceId: qhp.uuid,
                        eventType: 'misc',
                    };
                },
            )}
        />
    );
};
